import type { TFunction } from "i18next";
import type { IBuild } from "src/model/model";
import { scormSettingsOfBuild } from "./state/buildsSlice";

export interface IScormGuideInput {
  version: "1.2" | "2004";
  structure: "per_module" | "single";
  media: "embedded" | "streaming";
  aiAndVoiceTypes: string[];
  authType: string | null;
}

export interface IScormImportGuide {
  steps: string[];
  requirements: string[];
}

const AUTHENTICATED_AUTH_TYPES = ["classic", "simplified"];

export const scormGuideInputFromBuild = (
  build: IBuild,
  authType: string | null,
): IScormGuideInput | null => {
  const settings = scormSettingsOfBuild(build);
  if (!settings) {
    return null;
  }
  return {
    version: settings.version,
    structure: settings.structure,
    media: settings.media,
    aiAndVoiceTypes: build.scorm_options?.aiAndVoiceTypes ?? [],
    authType,
  };
};

export const buildScormImportGuide = (t: TFunction, input: IScormGuideInput): IScormImportGuide => {
  const hasAiAvatar = input.aiAndVoiceTypes.some((type) => type.startsWith("content_avatar_ai"));
  const requirements = [t("scormImportGuide.network")];
  if (input.media === "streaming") {
    requirements.push(t("scormImportGuide.networkStreaming"));
  }
  if (input.authType && AUTHENTICATED_AUTH_TYPES.includes(input.authType)) {
    requirements.push(t("scormImportGuide.authentication"));
  }
  if (hasAiAvatar) {
    requirements.push(t("scormImportGuide.aiAvatar"));
  }
  if (input.aiAndVoiceTypes.length > 0) {
    requirements.push(t("scormImportGuide.microphone"));
  }
  requirements.push(t("scormImportGuide.subscription"));

  return {
    steps: [
      t("scormImportGuide.step1", { version: input.version }),
      t(
        input.structure === "single"
          ? "scormImportGuide.step2Single"
          : "scormImportGuide.step2PerModule",
      ),
      t("scormImportGuide.step3"),
    ],
    requirements,
  };
};

export const scormImportGuideAsText = (t: TFunction, guide: IScormImportGuide): string =>
  [
    t("scormImportGuide.stepsTitle"),
    ...guide.steps.map((step, index) => `${index + 1}. ${step}`),
    "",
    t("scormImportGuide.requirementsTitle"),
    ...guide.requirements.map((requirement) => `- ${requirement}`),
  ].join("\n");
